import Button from "./common/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "./common/Dialog";
import { Info, X } from "lucide-react";
import useBrandTranslation from "../hooks/useBrandTranslation";

export default function PatientSummaryInfoDialog({ patient, className }) {
  const { t } = useBrandTranslation();
  if (!patient) return null;

  const fullName =
    [patient.name, patient.last_name].filter(Boolean).join(" ") ||
    patient.patient_name ||
    t("app.appointments.patientFallback", "Paciente");

  const emergencyName = [
    patient.emergency_contact_name,
    patient.emergency_contact_last_name,
  ]
    .filter(Boolean)
    .join(" ");

  const fields = [
    { label: t("app.clinical.dni", "DNI"), value: patient.dni || patient.document_number },
    { label: t("app.profile.height", "Altura (cm)"), value: patient.height_cm != null ? `${patient.height_cm} cm` : null },
    { label: t("app.profile.weight", "Peso (kg)"), value: patient.weight_kg != null ? `${patient.weight_kg} kg` : null },
    { label: t("app.clinical.healthInsurance", "Obra Social / Prepaga"), value: patient.health_insurance_name },
    { label: t("app.clinical.affiliateNumber", "Número de Afiliado"), value: patient.health_insurance_number },
    { label: t("app.clinical.plan", "Plan"), value: patient.health_insurance_plan },
    {
      label: t("app.clinical.emergencyContact", "Contacto de emergencia"),
      value: [emergencyName, patient.emergency_contact_phone]
        .filter(Boolean)
        .join(" — ") || null,
    },
  ];

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="xs"
          title={t("app.clinical.patientInfo", "Información del paciente")}
          className={className ?? "h-8 w-8 shrink-0 p-0"}
          onClick={(e) => e.stopPropagation()}
        >
          <Info className="h-4 w-4" aria-hidden />
          <span className="sr-only">{t("app.clinical.patientInfo", "Información del paciente")}</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-md max-h-[85vh] overflow-y-auto rounded-xl p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <DialogTitle className="pr-2 text-lg font-bold leading-snug text-gray-900">
            {t("app.clinical.patientInfoOf", "Información de {{name}}", { name: fullName })}
          </DialogTitle>
          <DialogClose asChild>
            <button
              type="button"
              className="shrink-0 rounded-full p-1 transition-colors hover:bg-gray-100"
              aria-label={t("common.close", "Cerrar")}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </DialogClose>
        </div>
        <dl className="space-y-3">
          {fields.map(({ label, value }) => (
            <div key={label}>
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="mt-0.5 break-words text-sm font-medium text-gray-900">
                {value?.toString().trim() ? value : "—"}
              </dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  );
}
